/**
 * 权限工具函数
 * 提供路由过滤、权限 key 提取、菜单生成等能力
 */

/**
 * 从路由配置中递归提取所有权限 key
 * @param {Array<{ path?: string, name?: string, meta?: { permission?: string, hidden?: boolean, title?: string }, children?: Array }>} routes - 路由配置数组
 * @returns {string[]} 权限 key 数组（去重）
 */
export function extractPermissionKeys(routes) {
  const keys = new Set();

  function walk(list) {
    for (const route of list) {
      if (route.meta?.permission) {
        keys.add(route.meta.permission);
      }
      if (route.children?.length) {
        walk(route.children);
      }
    }
  }

  walk(routes);
  return Array.from(keys);
}

/**
 * 根据用户权限过滤路由
 * @param {Array<{ path?: string, name?: string, meta?: { permission?: string, hidden?: boolean, title?: string }, children?: Array }>} routes - 完整路由配置
 * @param {string[]} permissions - 用户拥有的权限 key 列表
 * @returns {Array} 过滤后的路由配置
 */
export function filterRoutesByPermission(routes, permissions) {
  if (!permissions || !permissions.length) {
    return routes;
  }

  return routes
    .filter(route => {
      if (!route.meta?.permission) return true;
      return permissions.includes(route.meta.permission);
    })
    .map(route => {
      const filtered = { ...route };
      if (route.children?.length) {
        filtered.children = filterRoutesByPermission(route.children, permissions);
      }
      return filtered;
    });
}

/**
 * 从路由配置生成菜单树（过滤 hidden 路由）
 * @param {Array<{ path?: string, name?: string, meta?: { permission?: string, hidden?: boolean, title?: string }, children?: Array }>} routes - 路由配置数组
 * @returns {Array<{ path?: string, name?: string, meta?: object, children?: Array }>} 菜单树结构
 */
export function generateMenusFromRoutes(routes, parentPath = '') {
  const result = [];

  for (const route of routes) {
    if (route.meta?.hidden) continue;

    const fullPath = parentPath + (route.path.startsWith('/') ? route.path : '/' + route.path);

    const item = {
      path: fullPath,
      name: route.name,
      meta: route.meta,
    };

    if (route.children?.length) {
      const childMenus = generateMenusFromRoutes(route.children, fullPath);
      if (!childMenus.length) {
        continue;
      }
      item.children = childMenus;
    }

    result.push(item);
  }

  return result;
}

/**
 * 扁平化路由（用于 addRoute）
 * @param {Array<{ path?: string, name?: string, meta?: object, children?: Array }>} routes - 嵌套路由配置
 * @param {string} [parentPath=''] - 父级路径
 * @returns {Array<{ path: string, name?: string, meta?: object }>} 扁平化后的路由数组
 */
export function flatRoutes(routes, parentPath = '') {
  const result = [];

  for (const route of routes) {
    const path = parentPath + (route.path.startsWith('/') ? route.path : '/' + route.path);
    const flat = { ...route, path };

    if (route.children?.length) {
      result.push(...flatRoutes(route.children, path));
    } else {
      result.push(flat);
    }
  }

  return result;
}
